import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { backfillVideos, cleanUpMedia, MediaCleanupResult, rebuildRegionIndex, VideoBackfillResult } from '../services/adminService';

const formatBytes = (n: number) =>
  n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

/**
 * Orphaned upload cleanup. Photos/videos are uploaded straight to S3 before a post exists, so a
 * composer closed without posting leaves them behind, as do deleted posts and expired stories.
 * Always preview first: Delete is only offered for the result you just looked at.
 */
const AdminMediaTab: React.FC = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MediaCleanupResult | null>(null);

  const run = async (dryRun: boolean) => {
    if (!dryRun && !window.confirm(
      `Permanently delete ${result?.orphans ?? 0} unused upload${result?.orphans === 1 ? '' : 's'} (${formatBytes(result?.orphanBytes ?? 0)})? This can't be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await cleanUpMedia(dryRun));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Media cleanup failed');
    } finally {
      setBusy(false);
    }
  };

  const canDelete = !!result && result.dryRun && result.orphans > 0;

  const [videoBusy, setVideoBusy] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoResult, setVideoResult] = useState<VideoBackfillResult | null>(null);
  const runVideoBackfill = async (dryRun: boolean) => {
    if (!dryRun && !window.confirm(
      `Start processing for up to ${videoResult?.started ?? 0} video${videoResult?.started === 1 ? '' : 's'}? Each starts one or two AWS MediaConvert jobs, which cost a little.`)) return;
    setVideoBusy(true);
    setVideoError(null);
    try {
      setVideoResult(await backfillVideos(dryRun));
    } catch (err) {
      setVideoError(err instanceof Error ? err.message : 'Video backfill failed');
    } finally {
      setVideoBusy(false);
    }
  };
  const canStartVideos = !!videoResult && videoResult.dryRun && videoResult.started > 0;

  const [indexBusy, setIndexBusy] = useState(false);
  const [indexMessage, setIndexMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const rebuildIndex = async () => {
    setIndexBusy(true);
    setIndexMessage(null);
    try {
      const r = await rebuildRegionIndex();
      setIndexMessage({ ok: true, text: `Region index rebuilt: ${r.divers} diver${r.divers === 1 ? '' : 's'}, ${r.rows} membership${r.rows === 1 ? '' : 's'} written.` });
    } catch (err) {
      setIndexMessage({ ok: false, text: err instanceof Error ? err.message : 'Rebuilding the region index failed' });
    } finally {
      setIndexBusy(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Finds uploaded post and story photos/videos that no post references — from composers closed
        without posting, deleted posts and expired stories. Anything uploaded in the last 7 days is
        left alone, and profile, group, club and event photos are never touched.
      </Typography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={() => run(true)} disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : undefined}>
          Preview
        </Button>
        <Button variant="contained" color="error" onClick={() => run(false)} disabled={busy || !canDelete}>
          Delete {canDelete ? `${result!.orphans} unused` : 'unused'}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {result && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {result.dryRun ? 'Preview — nothing was deleted' : `Deleted ${result.deleted} of ${result.orphans} unused uploads`}
          </Typography>
          <Typography variant="body2">
            {result.objectsScanned} uploads checked · {result.objectsInUse} in use · {result.objectsTooRecent} too recent to judge ·{' '}
            <strong>{result.orphans} unused ({formatBytes(result.orphanBytes)})</strong>
          </Typography>
          {result.hitRunLimit && (
            <Alert severity="info" sx={{ mt: 1.5 }}>This run hit its safety limit — run it again to continue.</Alert>
          )}
          {result.dryRun && result.sampleOrphanKeys.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">Examples</Typography>
              {result.sampleOrphanKeys.map(k => (
                <Typography key={k} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>{k}</Typography>
              ))}
            </Box>
          )}
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Older videos</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Videos posted before video processing existed play as uploaded — iPhone clips often won't play outside Safari,
          and there is no poster image. This finds videos that are still in use and missing a web-friendly copy or a
          poster, and starts only what is missing. It never touches the original, skips anything uploaded in the last
          30 minutes, and starts at most 25 videos at a time (run it again for more). Preview first.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" disabled={videoBusy} onClick={() => runVideoBackfill(true)}
            startIcon={videoBusy ? <CircularProgress size={14} /> : undefined}>
            Preview
          </Button>
          <Button variant="contained" size="small" disabled={videoBusy || !canStartVideos} onClick={() => runVideoBackfill(false)}>
            Start processing
          </Button>
        </Stack>
        {videoError && <Alert severity="error" sx={{ mt: 1.5 }}>{videoError}</Alert>}
        {videoResult && (
          <Box sx={{ mt: 1.5 }}>
            <Alert severity={videoResult.failed > 0 ? 'warning' : 'info'}>
              {videoResult.dryRun
                ? `${videoResult.videosFound} video${videoResult.videosFound === 1 ? '' : 's'} in use · ${videoResult.alreadyProcessed} already processed · ${videoResult.started} would start (${videoResult.needVideo} need a web copy, ${videoResult.needPoster} need a poster)`
                : `Started ${videoResult.started} video${videoResult.started === 1 ? '' : 's'}${videoResult.failed ? `, ${videoResult.failed} failed to start` : ''}.`}
              {videoResult.tooRecent > 0 && ` ${videoResult.tooRecent} too recent to touch.`}
              {videoResult.missingOriginal > 0 && ` ${videoResult.missingOriginal} referenced but missing from storage.`}
              {videoResult.hitRunLimit && ' More remain — run it again.'}
            </Alert>
            {videoResult.dryRun && videoResult.sampleKeys.map(k => (
              <Typography key={k} variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>{k}</Typography>
            ))}
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Region index</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The community pages read who belongs to each region from an index that fills itself as divers join, leave and
          open the app. Rebuilding re-writes everyone's joined communities from their preferences — it repairs gaps and
          is safe to repeat. (Home regions can only be reported by the app, so those appear as each diver next visits.)
        </Typography>
        <Button variant="outlined" size="small" disabled={indexBusy} onClick={rebuildIndex}
          startIcon={indexBusy ? <CircularProgress size={14} /> : undefined}>
          Rebuild region index
        </Button>
        {indexMessage && <Alert severity={indexMessage.ok ? 'success' : 'error'} sx={{ mt: 1.5 }}>{indexMessage.text}</Alert>}
      </Paper>
    </Box>
  );
};

export default AdminMediaTab;
